/**
 * Medications Hook
 *
 * Fetches and caches the list of available medications from the database.
 * Used to match medication names to their IDs when creating patient medications.
 */

import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../../api/louise-api";
import type { ResponseBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";
import { throwApiError } from "../../api/api-error";

// ============================================================================
// Types
// ============================================================================

export interface ExternalCode {
  system: string;
  value: string;
  display: string | null;
  country: string | null;
  isPrimary: boolean | null;
}

export type Medication = ResponseBody<
  paths["/{tenantId}/medications"]["get"]
>["data"][number];

// ============================================================================
// Query Keys
// ============================================================================

export const medicationKeys = {
  all: ["medications"] as const,
  list: (tenantId: string) =>
    [...medicationKeys.all, "list", tenantId] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch all available medications.
 * Results are cached for 30 minutes since the list rarely changes.
 */
export function useMedications(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const { client, tenantId, patientId } = useApiClient();

  return useQuery({
    queryKey: medicationKeys.list(tenantId ?? ""),
    queryFn: async () => {
      if (!tenantId) return [];

      try {
        const response = await (
          client as {
            GET: (
              url: string,
              opts: Record<string, unknown>,
            ) => Promise<{ data?: unknown; error?: unknown }>;
          }
        ).GET("/{tenantId}/medications", {
          params: {
            path: { tenantId },
          },
        });

        if (response.error) {
          throwApiError(
            response.error,
            "Failed to load the medication catalog",
            "medication-catalog",
          );
        }

        // Handle both direct array and wrapped { data: [...] } responses
        const data = response.data;
        if (Array.isArray(data)) {
          return data as Medication[];
        }
        if (
          data &&
          typeof data === "object" &&
          Array.isArray((data as Record<string, unknown>).data)
        ) {
          return (data as Record<string, unknown>).data as Medication[];
        }
        throwApiError(
          { message: "The medication catalog response was not an array" },
          "Failed to load the medication catalog",
          "medication-catalog",
        );
      } catch (error) {
        throwApiError(
          error,
          "Failed to load the medication catalog",
          "medication-catalog",
        );
      }
    },
    // tenantId falls back to the default tenant when unauthenticated, so it
    // never gates anything — require an authenticated patient too (#1129).
    enabled: enabled && !!tenantId && !!patientId,
    staleTime: 1000 * 60 * 30, // 30 minutes - medications list rarely changes
    gcTime: 1000 * 60 * 60, // 1 hour cache
  });
}

/**
 * Find a medication by name (case-insensitive).
 * Returns the medication object if found, null otherwise.
 */
export function findMedicationByName(
  medications: Medication[],
  name: string,
): Medication | null {
  const lowerName = name.toLowerCase();
  return medications.find((m) => m.name?.toLowerCase() === lowerName) ?? null;
}
